import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SOCIAL_PROFILES } from '../../graphql';
import { SocialPlatformToggle } from './SocialPlatformToggle';
import { AccountSwitcher } from './AccountSwitcher';
import { useNavigate } from 'react-router-dom';
import { useAccount, UserAccount } from '../../context/AccountContext';
import { GetSocialProfilesResponse } from '../../types/graphql';
import { ApolloError } from '@apollo/client';
import { Plus } from 'lucide-react';

/**
 * TinySocial's platform toggle and New Post. The Ink review did not redraw
 * this bar, so it stays under the top shell on Today, Calendar, and
 * Automations. See src/styles/INK.md.
 */
interface HeaderProps {
  showPlatformToggle?: boolean;
  selectedProfiles?: Set<string>;
  onProfileToggle?: (profiles: Set<string>) => void;
  selectedAccount?: UserAccount | null;
  onAccountChange?: (account: UserAccount | null) => void;
  selectedPlatforms?: string;
  onPlatformChange?: (event: any, newPlatforms: string | string[]) => void;
  onCreatePost?: () => void;
}

export function Header({
  showPlatformToggle = false,
  selectedProfiles = new Set(),
  onProfileToggle,
  selectedAccount: selectedAccountProp = null,
  onAccountChange,
  onCreatePost,
}: HeaderProps) {
  const navigate = useNavigate();
  const { selectedAccount: contextAccount, error: accountsError } = useAccount();

  // Prefer the live context account; fall back to a prop for callers that still
  // pass one (they stay in sync via AccountContext).
  const selectedAccount = contextAccount ?? selectedAccountProp;

  // Keep legacy onAccountChange callers wired to the shared account context.
  useEffect(() => {
    if (onAccountChange && contextAccount) {
      onAccountChange(contextAccount);
    }
  }, [contextAccount, onAccountChange]);

  // Handle authentication errors from account membership load
  useEffect(() => {
    const checkAuthError = (errorObj: ApolloError | undefined): boolean => {
      if (!errorObj) return false;

      if (errorObj.message?.toLowerCase().includes('not authenticated')) {
        return true;
      }

      if (errorObj.graphQLErrors) {
        return errorObj.graphQLErrors.some((err) =>
          err.message?.toLowerCase().includes('not authenticated')
        );
      }

      return false;
    };

    if (checkAuthError(accountsError)) {
      sessionStorage.clear();
      localStorage.removeItem('token');
      //navigate('/login');
    }
  }, [accountsError, navigate]);

  const { data: socialProfilesData } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId: selectedAccount?.id },
    skip: !selectedAccount?.id || !showPlatformToggle,
  });

  return (
    <div className="bg-core-dark mx-1 p-4 flex items-center flex-row max-md:flex-col max-md:gap-4">
      <div className="flex flex-row flex-1 max-md:flex-col-reverse max-md:gap-4">
        {/* Profile Selection - only shown when showPlatformToggle is true */}
        {showPlatformToggle &&
          socialProfilesData?.socialProfiles &&
          socialProfilesData.socialProfiles.length > 0 && (
            <div className="mb-0 p-0 pt-0 m-0 ml-2 flex flex-row justify-between items-start">
              <SocialPlatformToggle
                profiles={socialProfilesData.socialProfiles}
                selectedProfiles={selectedProfiles}
                onProfileChange={onProfileToggle}
              />
            </div>
          )}
        <div className="relative flex flex-row items-center justify-end gap-2 mr-4 flex-1 justify-self-end max-md:self-center max-md:mr-0">
          <AccountSwitcher variant="header" />
          {onCreatePost && (
            <button
              onClick={onCreatePost}
              className="hidden md:flex px-3 py-1.5 rounded bg-primary text-onfill hover:bg-primary-dark transition-colors border border-primary items-center gap-2 flex-shrink-0"
              title="New Post"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">New Post</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
